import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as eventActions from 'Actions/events';
import { PlayInput } from 'Types';
import { InputChangeSource } from 'Types/EventTypes';
import { selectAccountPlayInput } from 'Utils/playAlong/effectiveGrading';
import { saveDeviceSetup, savePlayInput } from 'Utils/playAlong/playInputAccount';
import { reportError } from 'Utils/reportError';

/** Resolves true once the answer is saved, false when the save failed. */
export type ChangePlayInput = (next: PlayInput, source: InputChangeSource) => Promise<boolean>;

/**
 * Change what the account says the player plays on. Every entry point (the
 * device screen, the picker, the MIDI blocker, the keyboard prompt) changes the
 * one account value through this.
 *
 * The app takes the new value only once the backend has saved it, then records
 * the change (its source and the page it was made on). A save that fails
 * changes nothing and records nothing: the caller says so and the player can try
 * again.
 */
export function usePlayInputChange(): ChangePlayInput {
  const dispatch = useDispatch();
  const accountPlayInput = useSelector(selectAccountPlayInput);
  const accountPlayInputRef = useRef(accountPlayInput);
  accountPlayInputRef.current = accountPlayInput;

  return useCallback<ChangePlayInput>(
    async (next, source) => {
      const previous = accountPlayInputRef.current;
      const fromDeviceScreen = source === 'device_screen';
      try {
        const saved = fromDeviceScreen ? await saveDeviceSetup(next) : await savePlayInput(next);
        dispatch(
          appActions.applyPlayInputAnswer(
            fromDeviceScreen
              ? { play_input: saved, welcome_flow_setup_complete: true }
              : { play_input: saved },
          ),
        );
        dispatch(
          eventActions.inputChangedEventAction({
            playInput: saved,
            previousPlayInput: previous,
            source,
            surface: window.location.pathname,
          }),
        );
        return true;
      } catch (error) {
        reportError(error, 'playInput.save');
        return false;
      }
    },
    [dispatch],
  );
}
